import { FC, useMemo, memo, ReactNode, useState, useCallback, useRef } from 'react'
import styles from 'components/features/pfm/PostLayout/PostLayout.styl'
import Anchor from 'components/atoms/navigation/Anchor'
import FeedPostInfoBar from 'components/features/socialFeed/FeedPost/FeedPostInfoBar'
import { getFeedCommentAnchorId } from 'helpers/feedHelpers'
import { PostUidOwnProps, createPostAuthorSelector } from 'selectors/feedSelectors'
import { CommentCompose } from 'components/features/socialFeed/CommentCompose/CommentCompose'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import FeedPostCommentListContainer from 'components/features/socialFeed/FeedPostCommentList/FeedPostCommentListContainer'
import { FeedLinkLoggedOut } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import { CommentComposeLayout } from 'components/features/socialFeed/CommentComposeLayout/CommentComposeLayout'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { ActionIcon } from 'components/features/pfm/ActionMenu/ActionIcon'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import cn from 'classnames'
import { OuterEvent } from 'components/features/pfm/OuterEvent/OuterEvent'
import { FeedPostActionsMenu } from 'components/features/pfm/PostActionsMenu/PostActionsMenu'
import { filterEventsFromNode } from 'helpers/DOMHelpers'

type PostLayoutProps =
  & PostUidOwnProps
  & {
    header?: ReactNode
  }

export const PostLayout: FC<PostLayoutProps> = memo(({
  postUid,
  children,
  header,
}) => {
  const isAuthenticated = useStoreSelector(isAuthenticatedSelector)

  const postAuthorSelector = useMemo(
    createPostAuthorSelector,
    [postUid]
  )
  const author = useStoreSelector((state) => postAuthorSelector(state, { postUid }))

  const postInfoBar = useMemo(() => {
    if (!author || !author.is_followed || !author.username) {
      return null
    }
    return (
      <FeedPostInfoBar>
        You're following <span className={ styles.username }>{ author.username }</span>
      </FeedPostInfoBar>
    )
  }, [author])

  const cardCorners = useMemo(() => !!author && author.is_followed ? 'bottom' : 'all', [author])
  const composeId = useMemo(() => `newComment${ postUid }`, [postUid])

  const [actionMenuShown, setActionMenuShown] = useState(false)
  const handleActionsMenuShow = useCallback(() => setActionMenuShown(true), [setActionMenuShown])
  const handleActionsMenuHide = useCallback(() => setActionMenuShown(false), [setActionMenuShown])
  const actionMenuClassName = useMemo(() => cn(styles.actions, {
    [styles.actionMenuShown]: actionMenuShown,
  }), [actionMenuShown])

  const actionsMenuRootRef = useRef<HTMLDivElement>(null)

  return (
    <>
      { postInfoBar }
      <CardBlock withPadding forceRoundedCorners shadowTheme='medium' corners={ cardCorners } className={ styles.feedPost }>
        { header && (
          <header className={ styles.header }>
            { header && (
              <div className={ styles.info }>
                { header }
              </div>
            )}

            { isAuthenticated && (
              <div className={ actionMenuClassName }>
                <OuterEvent
                  onOuterClick={ filterEventsFromNode(actionsMenuRootRef, handleActionsMenuHide) }
                  onOuterTap={ filterEventsFromNode(actionsMenuRootRef, handleActionsMenuHide) }
                  onOuterESC={ filterEventsFromNode(actionsMenuRootRef, handleActionsMenuHide) }
                >
                  <button
                    className={ styles.actionButton }
                    onClick={ handleActionsMenuShow }
                  >
                    <ActionIcon />
                  </button>
                </OuterEvent>
                <div className={ styles.actionMenu } ref={ actionsMenuRootRef }>
                  <FeedPostActionsMenu postUid={ postUid } onClose={ handleActionsMenuHide } />
                </div>
              </div>
            )}
          </header>
        )}
        { children }

        <FeedPostCommentListContainer postUid={ postUid } />

        <div className={ styles.commentCompose }>
          <Anchor placement={ getFeedCommentAnchorId(postUid) }>
            <CommentComposeLayout composeId={ composeId }>
              <FeedLinkLoggedOut type='post' postUid={ postUid }>
                <CommentCompose
                  composeId={ composeId }
                  placeholder='Write a comment...'
                  postUid={ postUid }
                />
              </FeedLinkLoggedOut>
            </CommentComposeLayout>
          </Anchor>
        </div>
      </CardBlock>
    </>
  )
})


